import React, { useCallback, useMemo, useState } from 'react';
import { Text, Box } from 'components';
import { AddProfileIcon, EditProfileIcon, SettingsIcon } from 'components/Icons';
import { useI18n } from 'components/I18n';
import { ClickTracker } from 'components/Tracking';
import { shallowEqual, useSelector } from 'react-redux';
import AvatarImage from '../../ProfilesView/AvatarImage';
import { useProfileContext } from '../../ProfilesView/ProfileContext';
import { cloneRouterLocation } from '../../ProfilesView/utils';
import UserProfile from './UserProfile';
import MenuDropdown from './MenuDropdown';
import {
  ProfileMenuLink,
  SignUpButton,
} from './Styles';

const MENU_ICON_SIZE = '1.125rem';

function UserMenu() {
  const i18n = useI18n();
  const {
    profiles,
    currentProfile,
    selectProfile,
    profilesLoading,
    isFeatureEnabled,
    hasReachedProfileLimit,
    setReturnLocation,
  } = useProfileContext();

  const {
    isLoggedIn,
    isAnonymousUser,
    settingsMenu,
    location,
    navigation,
  } = useSelector(state => ({
    isLoggedIn: state.auth.isLoggedIn,
    isAnonymousUser: state.auth.isAnonymousUser,
    settingsMenu: state.settings.settingsMenu,
    location: state.router.location,
    navigation: state.settings.navigation,
  }), shallowEqual);

  const [isOpen, setIsOpen] = useState(false);
  const showProfileManagement = isFeatureEnabled && !!currentProfile && !profilesLoading;

  const otherProfiles = useMemo(
    () => (showProfileManagement ? (profiles || []).filter(profile => !profile?.current) : []),
    [profiles, showProfileManagement],
  );

  const settingsItem = navigation?.menu?.find(item => item.type === 'settings');

  const accountTitle = useMemo(
    () => settingsItem?.title || settingsMenu?.title || i18n.formatText('settings'),
    [settingsItem?.title, settingsMenu?.title, i18n],
  );

  const openMenu = useCallback(() => setIsOpen(true), []);
  const closeMenu = useCallback(() => setIsOpen(false), []);

  const handleSelectProfile = useCallback(profileId => async (event) => {
    event.preventDefault();
    closeMenu();
    await selectProfile(profileId);
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }, [closeMenu, selectProfile]);

  const createNavigateToProfileRouteHandler = useCallback(() => () => {
    closeMenu();
    setReturnLocation(cloneRouterLocation(location));
  }, [closeMenu, location, setReturnLocation]);

  const handleNavigation = useCallback(() => {
    closeMenu();
  }, [closeMenu]);

  if (!isLoggedIn || isAnonymousUser) {
    return null;
  }

  return (
    <MenuDropdown
      isOpen={isOpen}
      trigger={<UserProfile />}
      onClose={closeMenu}
      onOpen={openMenu}
    >
        {showProfileManagement && (
          <>
            {otherProfiles.map(profile => (
              <ClickTracker
                key={`profile-${profile.id}`}
                component="Menu"
                clickType="navigation"
                eventName="SelectProfile"
              >
                <ProfileMenuLink
                  to={{ name: location.name, params: location.params }}
                  data-content={profile.name}
                  onClick={handleSelectProfile(profile.id)}
                  role="menuitem"
                >
                  <Box
                    flexBox
                    alignItems="center"
                    gap="medium"
                    fullWidth
                  >
                    <Box>
                      <AvatarImage
                        src={profile.imageUrl}
                        size={MENU_ICON_SIZE}
                        alt=""
                      />
                    </Box>
                    <Text ellipsis="true">
                      {profile.name}
                    </Text>
                  </Box>
                </ProfileMenuLink>
              </ClickTracker>
            ))}

            {!hasReachedProfileLimit && (
              <ClickTracker
                component="Menu"
                clickType="navigation"
                eventName="AddProfile"
              >
                <ProfileMenuLink
                  to={{ name: 'profile-new' }}
                  data-content={i18n.formatText('profiles.newProfile')}
                  onClick={createNavigateToProfileRouteHandler()}
                  role="menuitem"
                >
                  <Box flexBox alignItems="center" gap="medium">
                    <AddProfileIcon width={MENU_ICON_SIZE} height={MENU_ICON_SIZE} />
                    <Text id="profiles.newProfile" />
                  </Box>
                </ProfileMenuLink>
              </ClickTracker>
            )}

            <ClickTracker
              component="Menu"
              clickType="navigation"
              eventName="EditProfiles"
            >
              <ProfileMenuLink
                to={{ name: 'profiles-edit' }}
                data-content={i18n.formatText('profiles.editProfiles')}
                onClick={createNavigateToProfileRouteHandler()}
                role="menuitem"
              >
                <Box flexBox alignItems="center" gap="medium">
                  <EditProfileIcon color="inherit" />
                  <Text id="profiles.editProfiles" />
                </Box>
              </ProfileMenuLink>
            </ClickTracker>
          </>
        )}

        {settingsItem && (
          <ClickTracker
            component="Menu"
            clickType="navigation"
            eventName="Settings"
          >
            <ProfileMenuLink
              to={{ name: 'settings-account' }}
              data-content={accountTitle}
              active={location.name.startsWith('settings')}
              onClick={handleNavigation}
              role="menuitem"
            >
              <Box flexBox alignItems="center" gap="medium">
                <SettingsIcon width={MENU_ICON_SIZE} height={MENU_ICON_SIZE} color="inherit" />
                <Text ellipsis="true">{accountTitle}</Text>
              </Box>
            </ProfileMenuLink>
          </ClickTracker>
        )}

        <ClickTracker
          component="Menu"
          clickType="navigation"
          eventName="click_sign_out_button"
        >
          <SignUpButton
            to={{ name: 'log-out' }}
            data-content={i18n.formatText('logOut')}
            onClick={handleNavigation}
            role="menuitem"
            position="vertical"
          >
            <Text id="logOut" />
          </SignUpButton>
        </ClickTracker>
      </MenuDropdown>
  );
}

export default React.memo(UserMenu);


